import { useState, type FormEvent } from "react";
import { ArrowLeft } from "lucide-react";
import { actions, listInvoices, listSupplierInvoiceSubmissions, supplierInvoiceEvidenceUrl } from "@/lib/api";
import type { Invoice, SupplierInvoiceSubmission } from "@/lib/types";
import { displayDate, money } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useData, matches, reportActionError, type ViewProps } from "@/lib/app-view";
import { PageHeader } from "@/components/page-header";
import { Panel } from "@/components/panel";
import { Empty, Loading, ErrorState } from "@/components/empty-state";
import { Table } from "@/components/data-table";
import { FieldError } from "@/components/field-error";
import { AppLink } from "@/components/app-link";
import { ControlPassportPanel } from "@/components/control-passport-panel";

export function Invoices({
  organisation,
  refresh,
  query,
  recordId,
  can,
  changed,
  navigate,
}: ViewProps & {
  recordId: string;
  navigate: (view: string, recordId?: string) => void;
}) {
  const { data, error, loading } = useData(
    async () => {
      const [invoices, submissions] = await Promise.all([
        listInvoices(organisation.id),
        listSupplierInvoiceSubmissions(organisation.id),
      ]);
      return { invoices, submissions };
    },
    [organisation.id, refresh],
  );
  const [busy, setBusy] = useState("");
  const [correctionInvoice, setCorrectionInvoice] = useState<Invoice>();
  const [overrideInvoice, setOverrideInvoice] = useState<Invoice>();
  if (loading) return <Loading />;
  if (error || !data) return <ErrorState message={error} />;
  const rows = data.invoices.filter((row) =>
    matches(
      query,
      row.id,
      row.invoice_no,
      row.purchase_orders?.po_no,
      row.vendors?.name,
      row.status,
    ),
  );
  async function act(row: Invoice, action: "match" | "approve") {
    setBusy(row.id);
    try {
      if (action === "match")
        await actions.matchInvoice(organisation.id, row.id);
      else await actions.approveInvoice(organisation.id, row.id);
      changed();
    } catch (reason) {
      reportActionError(reason);
    } finally {
      setBusy("");
    }
  }
  if (recordId) {
    const invoice = data.invoices.find((row) => row.id === recordId);
    if (!invoice)
      return (
        <PageHeader
          eyebrow="SOURCE INVOICE"
          title="Invoice unavailable"
          description="This invoice is not available in the current organisation."
        >
          <Button variant="outline" asChild>
            <AppLink view="invoices" navigate={navigate}>
              <ArrowLeft className="size-4" aria-hidden="true" />
              All invoices
            </AppLink>
          </Button>
        </PageHeader>
      );
    return (
      <>
        <InvoiceRecord
          row={invoice}
          organisationId={organisation.id}
          busy={busy}
          can={can}
          navigate={navigate}
          onAct={act}
          onCorrect={setCorrectionInvoice}
          onOverride={setOverrideInvoice}
        />
        <InvoiceCorrectionDialog
          invoice={correctionInvoice}
          organisationId={organisation.id}
          onOpenChange={(open) => !open && setCorrectionInvoice(undefined)}
          onDone={() => {
            setCorrectionInvoice(undefined);
            changed();
          }}
        />
        <InvoiceOverrideDialog
          invoice={overrideInvoice}
          organisationId={organisation.id}
          onOpenChange={(open) => !open && setOverrideInvoice(undefined)}
          onDone={() => {
            setOverrideInvoice(undefined);
            changed();
          }}
        />
      </>
    );
  }
  return (
    <>
      <PageHeader
        eyebrow="MATCH"
        title="Invoices & aggregate match"
        description="Compares the recorded invoice amount with the PO total and requires full PO receipt. Tax and line-level quantities are recorded but are not automatically validated."
      />
      <SupplierInvoiceInbox
        organisationId={organisation.id}
        submissions={data.submissions}
        canReview={can("finance")}
        onDone={changed}
      />
      {rows.length ? (
        <>
          <div className="hidden md:block">
            <Table
              heads={[
                "Invoice",
                "Vendor / PO",
                "Invoice value",
                "PO value",
                "Status",
                "Action",
              ]}
            >
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <AppLink
                      view="invoices"
                      recordId={row.id}
                      navigate={navigate}
                      ariaLabel={`Open invoice ${row.invoice_no}`}
                      className="inline-block"
                    >
                      <strong>{row.invoice_no}</strong>
                      <small className="block text-[var(--text-secondary)]">
                        {displayDate(row.invoice_date)}
                      </small>
                    </AppLink>
                  </td>
                  <td>
                    {row.vendors?.name}
                    <small className="block text-[var(--text-secondary)]">
                      <AppLink
                        view="orders"
                        recordId={row.purchase_order_id}
                        navigate={navigate}
                      >
                        {row.purchase_orders?.po_no}
                      </AppLink>
                    </small>
                  </td>
                  <td>
                    {money(Number(row.amount) + Number(row.tax_amount))}
                    <small className="block text-[var(--text-secondary)]">
                      {money(row.amount)} + {money(row.tax_amount)} tax
                    </small>
                  </td>
                  <td>{money(row.purchase_orders?.total)}</td>
                  <td>
                    <InvoiceMatchState row={row} />
                  </td>
                  <td>
                    <InvoiceActions
                      row={row}
                      busy={busy}
                      can={can}
                      onAct={act}
                      onCorrect={setCorrectionInvoice}
                      onOverride={setOverrideInvoice}
                    />
                  </td>
                </tr>
              ))}
            </Table>
          </div>
          <div className="grid gap-3 md:hidden" aria-label="Invoices">
            {rows.map((row) => (
              <article
                key={row.id}
                className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-sm)]"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <AppLink
                      view="invoices"
                      recordId={row.id}
                      navigate={navigate}
                      ariaLabel={`Open invoice ${row.invoice_no}`}
                      className="block"
                    >
                      <span className="block text-xs font-bold tracking-[.08em] text-[var(--text-muted)]">
                        {row.invoice_no}
                      </span>
                      <span className="mt-1 block text-base font-semibold">{row.vendors?.name}</span>
                    </AppLink>
                    <p className="mt-1 text-xs text-[var(--text-secondary)]">
                      <AppLink
                        view="orders"
                        recordId={row.purchase_order_id}
                        navigate={navigate}
                      >
                        {row.purchase_orders?.po_no}
                      </AppLink>{" "}
                      · {displayDate(row.invoice_date)}
                    </p>
                  </div>
                  <Badge value={row.status} />
                </div>
                <dl className="mt-4 grid grid-cols-2 gap-4 text-sm tabular-nums">
                  <div>
                    <dt className="text-xs text-[var(--text-muted)]">Invoice total</dt>
                    <dd className="mt-0.5 font-semibold">
                      {money(Number(row.amount) + Number(row.tax_amount))}
                    </dd>
                    <dd className="mt-0.5 text-xs text-[var(--text-secondary)]">
                      {money(row.amount)} + {money(row.tax_amount)} tax
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-[var(--text-muted)]">PO total</dt>
                    <dd className="mt-0.5 font-semibold">
                      {money(row.purchase_orders?.total)}
                    </dd>
                  </div>
                </dl>
                <div className="mt-4 border-t border-[var(--border)] pt-4">
                  <InvoiceMatchState row={row} />
                  <div className="mt-3">
                    <InvoiceActions
                      row={row}
                      busy={busy}
                      can={can}
                      onAct={act}
                      onCorrect={setCorrectionInvoice}
                      onOverride={setOverrideInvoice}
                      mobile
                    />
                  </div>
                </div>
              </article>
            ))}
          </div>
        </>
      ) : (
        <Empty
          title="No invoices recorded"
          description="Record an invoice from an issued purchase order."
        />
      )}
      <InvoiceCorrectionDialog
        invoice={correctionInvoice}
        organisationId={organisation.id}
        onOpenChange={(open) => !open && setCorrectionInvoice(undefined)}
        onDone={() => {
          setCorrectionInvoice(undefined);
          changed();
        }}
      />
      <InvoiceOverrideDialog
        invoice={overrideInvoice}
        organisationId={organisation.id}
        onOpenChange={(open) => !open && setOverrideInvoice(undefined)}
        onDone={() => {
          setOverrideInvoice(undefined);
          changed();
        }}
      />
    </>
  );
}

function InvoiceRecord({
  row,
  organisationId,
  busy,
  can,
  navigate,
  onAct,
  onCorrect,
  onOverride,
}: {
  row: Invoice;
  organisationId: string;
  busy: string;
  can: ViewProps["can"];
  navigate: (view: string, recordId?: string) => void;
  onAct: (row: Invoice, action: "match" | "approve") => void;
  onCorrect: (row: Invoice) => void;
  onOverride: (row: Invoice) => void;
}) {
  return (
    <>
      <PageHeader
        eyebrow="SOURCE INVOICE"
        title={`${row.invoice_no} · ${row.vendors?.name || "Supplier"}`}
        description="Recorded invoice values, purchase-order lineage and aggregate match state."
      >
        <Button variant="outline" asChild>
          <AppLink view="invoices" navigate={navigate}>
            <ArrowLeft className="size-4" aria-hidden="true" />
            All invoices
          </AppLink>
        </Button>
      </PageHeader>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[var(--border)] bg-[var(--surface)] p-4">
        <InvoiceMatchState row={row} />
        <InvoiceActions
          row={row}
          busy={busy}
          can={can}
          onAct={onAct}
          onCorrect={onCorrect}
          onOverride={onOverride}
        />
      </div>
      <ControlPassportPanel
        organisationId={organisationId}
        checkpoint="invoice"
        requestNo={(row as any).purchase_orders?.rfqs?.requisitions?.request_no}
        viewerRole={
          can("manage") ? "tenant administrator" :
          can("finance") ? "finance" :
          can("approve") ? "approver" :
          can("procure") ? "procurement" : "requester"
        }
        capabilities={{
          manage: can("manage"),
          approve: can("approve"),
          procure: can("procure"),
          finance: can("finance"),
          receive: can("receive"),
        }}
        onNavigate={navigate}
      />

      <div className="grid gap-5 lg:grid-cols-2">
        <Panel>
          <p className="eyebrow">INVOICE FACTS</p>
          <h2 className="mt-1 font-serif text-xl">Recorded payable</h2>
          <dl className="mt-4 grid grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="text-xs text-[var(--text-muted)]">Invoice date</dt>
              <dd className="mt-1 font-medium">{displayDate(row.invoice_date)}</dd>
            </div>
            <div>
              <dt className="text-xs text-[var(--text-muted)]">Recorded</dt>
              <dd className="mt-1 font-medium">{displayDate(row.created_at, true)}</dd>
            </div>
            <div>
              <dt className="text-xs text-[var(--text-muted)]">Net amount</dt>
              <dd className="mt-1 font-medium tabular-nums">{money(row.amount)}</dd>
            </div>
            <div>
              <dt className="text-xs text-[var(--text-muted)]">Tax amount</dt>
              <dd className="mt-1 font-medium tabular-nums">{money(row.tax_amount)}</dd>
            </div>
            <div>
              <dt className="text-xs text-[var(--text-muted)]">Invoice total</dt>
              <dd className="mt-1 font-medium tabular-nums">
                {money(Number(row.amount) + Number(row.tax_amount))}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-[var(--text-muted)]">PO total</dt>
              <dd className="mt-1 font-medium tabular-nums">{money(row.purchase_orders?.total)}</dd>
            </div>
          </dl>
        </Panel>
        <Panel>
          <p className="eyebrow">MATCH EVIDENCE</p>
          <h2 className="mt-1 font-serif text-xl">Aggregate checks</h2>
          <div className="mt-4 grid gap-3 text-sm">
            <div className="rounded-lg border border-[var(--border)] p-3">
              <span className="text-xs text-[var(--text-muted)]">Source purchase order</span>
              <p className="mt-1 font-medium">
                <AppLink
                  view="orders"
                  recordId={row.purchase_order_id}
                  navigate={navigate}
                >
                  {row.purchase_orders?.po_no || "Open order"}
                </AppLink>
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-[var(--border)] p-3">
                <span className="text-xs text-[var(--text-muted)]">Value check</span>
                <p className="mt-1 font-medium">
                  {typeof row.match_detail?.amount_ok === "boolean"
                    ? row.match_detail.amount_ok
                      ? "Within tolerance"
                      : "Exception"
                    : "Not run"}
                </p>
              </div>
              <div className="rounded-lg border border-[var(--border)] p-3">
                <span className="text-xs text-[var(--text-muted)]">Receipt check</span>
                <p className="mt-1 font-medium">
                  {typeof row.match_detail?.receipt_ok === "boolean"
                    ? row.match_detail.receipt_ok
                      ? "Complete"
                      : "Incomplete"
                    : "Not run"}
                </p>
              </div>
            </div>
            <p className="text-xs text-[var(--text-secondary)]">
              RAQOZ records aggregate value and full-receipt checks. It does not claim automated tax validation or line-level three-way matching.
            </p>
          </div>
        </Panel>
      </div>
    </>
  );
}

function InvoiceMatchState({ row }: { row: Invoice }) {
  return (
    <div>
      <Badge value={row.status} />
      {typeof row.match_detail?.amount_ok === "boolean" && (
        <small className="mt-1 block text-[var(--text-secondary)]">
          Value {row.match_detail.amount_ok ? "within tolerance" : "exception"} ·
          Receipt {row.match_detail.receipt_ok ? "complete" : "incomplete"}
        </small>
      )}
      {row.match_detail?.override === true && (
        <small className="mt-1 block text-[var(--warning)]">
          Explicit management override recorded
        </small>
      )}
    </div>
  );
}

function InvoiceActions({
  row,
  busy,
  can,
  onAct,
  onCorrect,
  onOverride,
  mobile = false,
}: {
  row: Invoice;
  busy: string;
  can: ViewProps["can"];
  onAct: (row: Invoice, action: "match" | "approve") => void;
  onCorrect: (row: Invoice) => void;
  onOverride: (row: Invoice) => void;
  mobile?: boolean;
}) {
  return (
    <div className={`flex flex-wrap gap-2 ${mobile ? "[&>button]:min-h-11 [&>button]:flex-1" : ""}`}>
      {can("finance") && ["recorded", "exception"].includes(row.status) && (
        <Button
          size="sm"
          variant="outline"
          disabled={busy === row.id}
          onClick={() => onAct(row, "match")}
        >
          {busy === row.id ? "Matching…" : "Run match (aggregate)"}
        </Button>
      )}
      {can("finance") && ["recorded", "exception"].includes(row.status) && (
        <Button size="sm" variant="outline" onClick={() => onCorrect(row)}>
          Correct record
        </Button>
      )}
      {can("manage") && row.status === "exception" && (
        <Button size="sm" variant="outline" onClick={() => onOverride(row)}>
          Override exception
        </Button>
      )}
      {can("finance") && row.status === "matched" && (
        <Button
          size="sm"
          disabled={busy === row.id}
          onClick={() => onAct(row, "approve")}
        >
          {busy === row.id ? "Approving…" : "Approve"}
        </Button>
      )}
      {row.status === "approved" && (
        <span className="self-center text-xs text-[var(--text-muted)]">Complete</span>
      )}
    </div>
  );
}

function InvoiceCorrectionDialog({
  invoice,
  organisationId,
  onOpenChange,
  onDone,
}: {
  invoice?: Invoice;
  organisationId: string;
  onOpenChange: (open: boolean) => void;
  onDone: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!invoice) return;
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    try {
      await actions.correctInvoiceRecord(organisationId, invoice.id, {
        invoiceNo: String(form.get("invoiceNo") || ""),
        invoiceDate: String(form.get("invoiceDate") || ""),
        amount: Number(form.get("amount")),
        taxAmount: Number(form.get("taxAmount")),
        reason: String(form.get("reason") || ""),
      });
      onDone();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open={Boolean(invoice)} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader eyebrow="CONTROLLED CORRECTION" title={`Correct ${invoice?.invoice_no || "invoice"}`} />
        <form className="grid gap-4 p-6" onSubmit={submit}>
          <p className="text-sm text-slate-600">
            The previous values remain in an append-only correction record. A corrected invoice must be matched again.
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            <Label>Invoice number<Input name="invoiceNo" defaultValue={invoice?.invoice_no} required /></Label>
            <Label>Invoice date<Input name="invoiceDate" type="date" defaultValue={invoice?.invoice_date} required /></Label>
            <Label>Recorded amount<Input name="amount" type="number" min="0.01" step="0.01" defaultValue={invoice?.amount} required /></Label>
            <Label>Tax amount<Input name="taxAmount" type="number" min="0" step="0.01" defaultValue={invoice?.tax_amount} required /></Label>
          </div>
          <Label>Correction reason<Textarea name="reason" minLength={20} required placeholder="Explain the source evidence and why the recorded values require correction." /></Label>
          <FieldError>{error}</FieldError>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy}>{busy ? "Recording…" : "Record correction"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function InvoiceOverrideDialog({
  invoice,
  organisationId,
  onOpenChange,
  onDone,
}: {
  invoice?: Invoice;
  organisationId: string;
  onOpenChange: (open: boolean) => void;
  onDone: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!invoice) return;
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    try {
      await actions.overrideInvoiceMatchException(
        organisationId,
        invoice.id,
        String(form.get("reason") || ""),
      );
      onDone();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open={Boolean(invoice)} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader eyebrow="MANAGEMENT OVERRIDE" title={`Override ${invoice?.invoice_no || "match exception"}`} />
        <form className="grid gap-4 p-6" onSubmit={submit}>
          <p className="text-sm text-slate-600">
            This waives the open exception; it does not prove a line-level or tax match. The reason, actor, time and prior comparison are retained.
          </p>
          <Label>Override reason<Textarea name="reason" minLength={20} required placeholder="State the evidence reviewed and the accountable decision basis." /></Label>
          <FieldError>{error}</FieldError>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy}>{busy ? "Recording…" : "Record override"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SupplierInvoiceInbox({
  organisationId,
  submissions,
  canReview,
  onDone,
}: {
  organisationId: string;
  submissions: SupplierInvoiceSubmission[];
  canReview: boolean;
  onDone: () => void;
}) {
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState("");
  const reviewable = submissions.filter((row) =>
    ["submitted", "under_review", "returned"].includes(row.status),
  );
  async function evidence(row: SupplierInvoiceSubmission) {
    try {
      const path = row.attachments?.object_path;
      if (!path) throw new Error("Invoice evidence path is unavailable.");
      window.open(await supplierInvoiceEvidenceUrl(path), "_blank", "noopener");
    } catch (error) {
      reportActionError(error);
    }
  }
  async function decide(row: SupplierInvoiceSubmission, decision: string) {
    const reason = reasons[row.id]?.trim();
    if (!reason) return;
    setBusy(row.id + decision);
    try {
      await actions.decideSupplierInvoiceSubmission(
        organisationId,
        row.id,
        decision,
        reason,
      );
      onDone();
    } catch (error) {
      reportActionError(error);
    } finally {
      setBusy("");
    }
  }
  return (
    <Panel className="mb-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-serif text-xl">Supplier invoice inbox</h2>
          <p className="mt-1 text-sm text-slate-500">
            Original supplier files remain immutable. Extraction is marked unavailable until an OCR provider is configured.
          </p>
        </div>
        <Badge value={reviewable.length ? "action required" : "current"} />
      </div>
      <div className="mt-4 grid gap-3">
        {submissions.map((row) => {
          const duplicate = row.duplicate_flags.length > 0;
          const open = ["submitted", "under_review", "returned"].includes(
            row.status,
          );
          return (
            <div key={row.id} className="rounded-xl border border-slate-200 p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <strong>{row.invoice_no}</strong>
                  <small className="block text-slate-500">
                    {row.vendors?.name} · {row.purchase_orders?.po_no} · {displayDate(row.invoice_date)}
                  </small>
                  <small className="block text-slate-500">
                    {money(row.total_amount, row.currency)} · SHA-256 {row.original_sha256.slice(0, 12)}…
                  </small>
                </div>
                <div className="flex gap-2">
                  {duplicate && <Badge value="duplicate review" />}
                  <Badge value={row.status} />
                </div>
              </div>
              {duplicate && (
                <div className="mt-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
                  {row.duplicate_flags.map((flag) => flag.message).join(" ")}
                </div>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => evidence(row)}>
                  Open original evidence
                </Button>
              </div>
              {canReview && open && (
                <div className="mt-3 grid gap-2">
                  <Input
                    value={reasons[row.id] || ""}
                    onChange={(event) =>
                      setReasons((current) => ({
                        ...current,
                        [row.id]: event.target.value,
                      }))
                    }
                    placeholder="Review reason — required for every decision"
                  />
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      disabled={!reasons[row.id]?.trim() || busy.startsWith(row.id)}
                      onClick={() =>
                        decide(row, duplicate ? "accept_duplicate_override" : "accept")
                      }
                    >
                      {duplicate ? "Accept with duplicate override" : "Accept into match queue"}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!reasons[row.id]?.trim() || busy.startsWith(row.id)}
                      onClick={() => decide(row, "return")}
                    >
                      Return to supplier
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!reasons[row.id]?.trim() || busy.startsWith(row.id)}
                      onClick={() => decide(row, "reject")}
                    >
                      Reject
                    </Button>
                  </div>
                </div>
              )}
              {row.review_reason && (
                <p className="mt-2 text-xs text-slate-500">Decision: {row.review_reason}</p>
              )}
            </div>
          );
        })}
        {!submissions.length && (
          <p className="text-sm text-slate-500">No supplier invoices submitted.</p>
        )}
      </div>
    </Panel>
  );
}
